import { useState, useRef, useEffect } from 'react'
import { useAuthStore, type AccessibleTenant } from '../store/authStore'
import { switchTenant } from '../services/api'
import { errorText } from '../utils/errorText'

// Icons
const BuildingIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
)

const StarIcon = () => (
  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
)

const MSPIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
)

function getTenantTypeLabel(type: string): string {
  switch (type) {
    case 'msp':
      return 'MSP'
    case 'enterprise':
      return 'Enterprise'
    case 'standard':
      return 'Standard'
    default:
      return type
  }
}

function getTenantTypeBadgeColor(type: string): string {
  switch (type) {
    case 'msp':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    case 'enterprise':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
  }
}

export default function TenantSwitcher() {
  const { tenantContext, accessibleTenants, switchTenant: updateTenantContext } = useAuthStore()
  const [isOpen, setIsOpen] = useState(false)
  const [isSwitching, setIsSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Don't render if no tenant context or only one tenant
  if (!tenantContext || accessibleTenants.length <= 1) {
    // Just show the current tenant name without dropdown
    return (
      <div className="flex items-center space-x-2 px-3 py-2 text-sm text-theme-text-secondary">
        <BuildingIcon />
        <span className="text-theme-text-secondary">{tenantContext?.tenant_name || 'No Tenant'}</span>
      </div>
    )
  }

  const handleSwitchTenant = async (tenant: AccessibleTenant) => {
    if (tenant.id === tenantContext?.tenant_id) {
      setIsOpen(false)
      return
    }

    setIsSwitching(true)
    setError(null)

    try {
      const response = await switchTenant(tenant.id)
      updateTenantContext(response)
      setIsOpen(false)
      // Optionally reload the page to refresh all data
      window.location.reload()
    } catch (err: any) {
      setError(errorText(err, 'Failed to switch tenant'))
    } finally {
      setIsSwitching(false)
    }
  }

  // Group tenants: primary tenants first, then managed tenants
  const primaryTenants = accessibleTenants.filter(t => !t.is_managed)
  const managedTenants = accessibleTenants.filter(t => t.is_managed)

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className="flex items-center space-x-2 px-3 py-2 rounded-lg text-theme-text-secondary hover:bg-theme-tertiary hover:text-theme-text-primary transition-colors disabled:opacity-50"
      >
        <BuildingIcon />
        <span className="max-w-[150px] truncate text-sm font-semibold">{tenantContext.tenant_name}</span>
        <ChevronDownIcon />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-72 bg-theme-card border border-theme-border-primary rounded-lg shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-theme-border-primary bg-theme-tertiary">
            <p className="text-sm font-medium text-theme-text-primary">Switch Tenant</p>
            <p className="text-xs text-theme-text-secondary mt-1">
              Select a tenant to switch your context
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="px-4 py-2 bg-red-100 dark:bg-red-900/30 border-b border-red-200 dark:border-red-800">
              <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Tenant list */}
          <div className="max-h-80 overflow-y-auto">
            {/* Primary tenants */}
            {primaryTenants.length > 0 && (
              <div className="py-2">
                <p className="px-4 py-1 text-xs font-medium text-theme-text-secondary uppercase tracking-wider">
                  Your Tenants
                </p>
                {primaryTenants.map((tenant) => (
                  <TenantItem
                    key={tenant.id}
                    tenant={tenant}
                    isActive={tenant.id === tenantContext?.tenant_id}
                    isSwitching={isSwitching}
                    onSelect={() => handleSwitchTenant(tenant)}
                  />
                ))}
              </div>
            )}

            {/* Managed tenants (MSP) */}
            {managedTenants.length > 0 && (
              <div className="py-2 border-t border-theme-border-primary">
                <div className="px-4 py-1 flex items-center space-x-2">
                  <MSPIcon />
                  <p className="text-xs font-medium text-theme-text-secondary uppercase tracking-wider">
                    Managed Tenants
                  </p>
                </div>
                {managedTenants.map((tenant) => (
                  <TenantItem
                    key={tenant.id}
                    tenant={tenant}
                    isActive={tenant.id === tenantContext?.tenant_id}
                    isSwitching={isSwitching}
                    onSelect={() => handleSwitchTenant(tenant)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

interface TenantItemProps {
  tenant: AccessibleTenant
  isActive: boolean
  isSwitching: boolean
  onSelect: () => void
}

function TenantItem({ tenant, isActive, isSwitching, onSelect }: TenantItemProps) {
  return (
    <button
      onClick={onSelect}
      disabled={isSwitching}
      className={`w-full px-4 py-2 flex items-center justify-between text-left transition-colors ${
        isActive
          ? 'bg-blue-50 dark:bg-blue-900/20'
          : 'hover:bg-theme-tertiary'
      } disabled:opacity-50`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <span className={`text-sm font-medium truncate ${
            isActive ? 'text-blue-600 dark:text-blue-400' : 'text-theme-text-primary'
          }`}>
            {tenant.display_name || tenant.name}
          </span>
          {tenant.is_primary && (
            <span className="text-yellow-500" title="Primary tenant">
              <StarIcon />
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2 mt-0.5">
          <span className={`text-xs px-1.5 py-0.5 rounded ${getTenantTypeBadgeColor(tenant.type)}`}>
            {getTenantTypeLabel(tenant.type)}
          </span>
          <span className="text-xs text-theme-text-secondary capitalize">
            {tenant.role.replace('_', ' ')}
          </span>
        </div>
      </div>
      {isActive && (
        <span className="text-blue-600 dark:text-blue-400 ml-2">
          <CheckIcon />
        </span>
      )}
    </button>
  )
}
